import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Shield, Clock, CheckCircle, AlertTriangle, ArrowRight, ChevronDown } from 'lucide-react'
import { PageLayout } from '@/layouts'
import { Card, CardTitle, CardDescription, LoadingSpinner, ErrorDisplay } from '@/components/ui'
import { usePrivacyStats } from '@/hooks/use-privacy'

function StatCard({
  title,
  value,
  description,
  icon: Icon,
  color = 'primary',
  loading = false,
}: {
  title: string
  value: string | number
  description?: string
  icon: React.ComponentType<{ className?: string }>
  color?: 'primary' | 'green' | 'yellow' | 'red'
  loading?: boolean
}) {
  const colorStyles = {
    primary: {
      icon: 'text-primary-600 bg-primary-50 dark:bg-primary-900/20',
      value: 'text-primary-600',
    },
    green: {
      icon: 'text-green-600 bg-green-50 dark:bg-green-900/20',
      value: 'text-green-600',
    },
    yellow: {
      icon: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20',
      value: 'text-yellow-600',
    },
    red: {
      icon: 'text-red-600 bg-red-50 dark:bg-red-900/20',
      value: 'text-red-600',
    },
  }

  return (
    <Card>
      <div className="flex items-start justify-between">
        <div>
          <CardDescription>{title}</CardDescription>
          {loading ? (
            <div className="mt-2 h-9 flex items-center">
              <LoadingSpinner size="sm" />
            </div>
          ) : (
            <p className={`mt-2 text-3xl font-bold ${colorStyles[color].value}`}>{value}</p>
          )}
          {description && (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{description}</p>
          )}
        </div>
        <div className={`p-2 rounded-lg ${colorStyles[color].icon}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </Card>
  )
}

export function DashboardPage() {
  const [isPrivacyExpanded, setIsPrivacyExpanded] = useState(true)
  const { data: stats, isLoading, error, refetch } = usePrivacyStats()

  const pendingCount = stats ? (stats.by_status.received || 0) + (stats.by_status.in_progress || 0) : 0
  const completedCount = stats?.by_status.completed || 0

  // Show error state with option to continue
  const showError = error && !stats

  return (
    <PageLayout title="Dashboard" description="Welcome to the PMGSC Operations Portal">
      {/* Data Privacy Section */}
      <section className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {/* Section Header - Clickable */}
        <button
          onClick={() => setIsPrivacyExpanded(!isPrivacyExpanded)}
          className="w-full flex items-center justify-between gap-3 p-6 text-left hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30">
              <Shield className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Data Privacy</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Privacy request management and compliance tracking</p>
            </div>
          </div>
          <ChevronDown
            className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${isPrivacyExpanded ? '' : '-rotate-90'}`}
          />
        </button>

        {/* Collapsible Content */}
        <div
          className={`transition-all duration-200 ease-in-out ${
            isPrivacyExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
          }`}
        >
          <div className="p-6">
            {/* Error Display */}
            {showError && (
              <div className="mb-6">
                <ErrorDisplay
                  error={error as Error}
                  title="Failed to Load Statistics"
                  onRetry={() => refetch()}
                  compact
                />
              </div>
            )}

            {/* KPI Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <StatCard
                title="Privacy Subjects"
                value={stats?.total || 0}
                description="Total records"
                icon={Shield}
                color="primary"
                loading={isLoading}
              />
              <StatCard
                title="Pending"
                value={pendingCount}
                description="Awaiting action"
                icon={Clock}
                color="yellow"
                loading={isLoading}
              />
              <StatCard
                title="Completed"
                value={completedCount}
                description="Processed"
                icon={CheckCircle}
                color="green"
                loading={isLoading}
              />
              <StatCard
                title="Overdue"
                value={stats?.overdue_count || 0}
                description="Past due date"
                icon={AlertTriangle}
                color="red"
                loading={isLoading}
              />
            </div>

            {/* Alerts Section */}
            {stats && (stats.overdue_count > 0 || stats.pending_due_soon > 0) && (
              <div className="mt-6">
                <h3 className="text-base font-medium text-slate-900 dark:text-white mb-3">Alerts</h3>
                <div className="space-y-3">
                  {stats.overdue_count > 0 && (
                    <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium text-red-800 dark:text-red-200">
                          {stats.overdue_count} overdue request{stats.overdue_count !== 1 ? 's' : ''}
                        </p>
                        <p className="text-sm text-red-600 dark:text-red-400">
                          These requests have passed their due date
                        </p>
                      </div>
                      <Link
                        to="/privacy?filter=overdue"
                        className="text-sm font-medium text-red-600 dark:text-red-400 hover:underline"
                      >
                        View all
                      </Link>
                    </div>
                  )}

                  {stats.pending_due_soon > 0 && (
                    <div className="flex items-center gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <Clock className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium text-yellow-800 dark:text-yellow-200">
                          {stats.pending_due_soon} request{stats.pending_due_soon !== 1 ? 's' : ''} due soon
                        </p>
                        <p className="text-sm text-yellow-600 dark:text-yellow-400">
                          Due within the next 7 days
                        </p>
                      </div>
                      <Link
                        to="/privacy?filter=due_soon"
                        className="text-sm font-medium text-yellow-600 dark:text-yellow-400 hover:underline"
                      >
                        View all
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quick Links */}
            <div className="mt-6">
              <h3 className="text-base font-medium text-slate-900 dark:text-white mb-3">Quick Actions</h3>
              <div className="grid gap-4 md:grid-cols-3">
                <Link to="/privacy" className="group">
                  <Card className="h-full transition-all group-hover:border-primary-300 dark:group-hover:border-primary-600">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Privacy Subjects</CardTitle>
                        <CardDescription>View and manage all privacy requests</CardDescription>
                      </div>
                      <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-primary-500 transition-colors" />
                    </div>
                  </Card>
                </Link>

                <Link to="/privacy/stats" className="group">
                  <Card className="h-full transition-all group-hover:border-primary-300 dark:group-hover:border-primary-600">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Statistics</CardTitle>
                        <CardDescription>View analytics and metrics</CardDescription>
                      </div>
                      <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-primary-500 transition-colors" />
                    </div>
                  </Card>
                </Link>

                <Link to="/privacy/import" className="group">
                  <Card className="h-full transition-all group-hover:border-primary-300 dark:group-hover:border-primary-600">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Import CSV</CardTitle>
                        <CardDescription>Bulk import privacy subjects</CardDescription>
                      </div>
                      <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-primary-500 transition-colors" />
                    </div>
                  </Card>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  )
}
